import { useCallback, useEffect, useRef, useState } from "react";
import { playCue } from "./reelAudio";

// A gallery card's glance lasts three seconds. The chime lands near the end,
// so the whoosh on the way in and the chime on the way out bracket the glance.
export const GLANCE_MS = 3000;
export const CHIME_AT_MS = 2400;

// Browsers block sound until the viewer taps the page, and a hover is not a
// tap. Without the Activation API we assume a tap, as the reel does; if the
// browser still refuses, the Web Audio cue simply stays silent.
function pageHasBeenTapped() {
  if (typeof navigator === "undefined" || !navigator.userActivation) return true;
  return navigator.userActivation.hasBeenActive === true;
}

/**
 * Drives one card's glance. `start({ sound })` runs the three-second glance;
 * with `sound` set and the page tapped, a whoosh plays on the way in and a
 * chime near the end. `hoverStart` is the desktop hover path, and it also asks
 * for a "Tap for sound" chip when the page has not been tapped yet. `stop` ends
 * the glance and cancels any pending chime.
 */
export function useGlance({ enabled = true } = {}) {
  const [running, setRunning] = useState(false);
  const [runKey, setRunKey] = useState(0);
  const [needsTap, setNeedsTap] = useState(false);
  const timers = useRef([]);

  const clear = useCallback(() => {
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current = [];
  }, []);

  useEffect(() => clear, [clear]);

  const start = useCallback(
    ({ sound }) => {
      if (!enabled) return;
      clear();
      setRunning(true);
      setRunKey((key) => key + 1);
      if (sound && pageHasBeenTapped()) {
        playCue("whoosh");
        timers.current.push(setTimeout(() => playCue("chime"), CHIME_AT_MS));
      }
      timers.current.push(setTimeout(() => setRunning(false), GLANCE_MS));
    },
    [clear, enabled]
  );

  const hoverStart = useCallback(() => {
    if (!enabled) return;
    setNeedsTap(!pageHasBeenTapped());
    start({ sound: true });
  }, [enabled, start]);

  const stop = useCallback(() => {
    clear();
    setRunning(false);
    setNeedsTap(false);
  }, [clear]);

  // The chip's click is the tap the browser needs, so the chime can play now.
  const enableSound = useCallback(() => {
    setNeedsTap(false);
    playCue("chime");
  }, []);

  return { running, runKey, needsTap, start, hoverStart, stop, enableSound };
}
